/**
 * Resolve bar vs line chart from whole_data / label count (UN + EU).
 * D-25: ≤2 distinct years → bar (avoid a misleading line for sparse points).
 */

/** Match PHP WholeDataChart::FEW_POINTS_BAR_MAX. */
export const FEW_POINTS_BAR_MAX = 2;

export function distinctYearsFromWholeData(wholeData: string | null | undefined): number[] {
  if (!wholeData || typeof wholeData !== 'string' || !wholeData.trim()) {
    return [];
  }
  try {
    const obj = JSON.parse(wholeData);
    const yearCol = obj?.columns?.[0]?.data;
    if (!Array.isArray(yearCol)) {
      return [];
    }
    const years = yearCol
      .map((y: unknown) => {
        const n = parseInt(String(y).replace(/\D+/g, ''), 10);
        return Number.isFinite(n) && n > 0 ? n : null;
      })
      .filter((y: number | null): y is number => y !== null);
    return Array.from(new Set(years)).sort((a, b) => a - b);
  } catch {
    return [];
  }
}

export function hasYearGaps(years: number[]): boolean {
  if (years.length < 2) return false;
  const sorted = [...years].sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] > 1) return true;
  }
  return false;
}

export function resolveIsBarChart(
  isBarFromApi: boolean | undefined,
  wholeData?: string | null,
  labelCount?: number
): boolean {
  const years = wholeData ? distinctYearsFromWholeData(wholeData) : [];
  if (years.length > 0 && years.length <= FEW_POINTS_BAR_MAX) {
    return true;
  }
  if (years.length > 1 && hasYearGaps(years)) {
    return true;
  }
  if (typeof labelCount === 'number' && labelCount > 0 && labelCount <= FEW_POINTS_BAR_MAX) {
    return true;
  }
  return !!isBarFromApi;
}

export function chartTypeFromBarFlag(isBar: boolean): 'bar' | 'line' {
  return isBar ? 'bar' : 'line';
}

export function dataDisplayTypeFromBarFlag(isBar: boolean): 'bar-chart' | 'line-graph-bar-chart' {
  return isBar ? 'bar-chart' : 'line-graph-bar-chart';
}
